import { useMemo } from "react";
import { useSearch } from "wouter";
import type { Kind, Node, Space, StatusDef, View, ViewFilters } from "@melo/shared";
import { matches, readFilters } from "../lib/filters";
import type { Tables } from "../lib/store";
import { descendantIds, isDone, kindsOf, nodesOf } from "../lib/select";

/** Kinds that describe structure rather than work; hidden from "all kinds" boards. */
const STRUCTURAL = new Set(["phase", "doc", "system"]);

/** True when the view shows only the signed-in person's work (the Q shortcut). */
export function useMine(): boolean {
  const search = useSearch();
  return (readFilters(search)?.assignees ?? []).includes("me");
}

export function viewKinds(t: Tables, space: Space, view: View): Kind[] {
  const all = kindsOf(t, space.id);
  if (view.config.kindKeys.length) return all.filter((k) => view.config.kindKeys.includes(k.key));
  return view.type === "board" ? all.filter((k) => !STRUCTURAL.has(k.key)) : all;
}

/** The nodes a view shows, in rank order, after the view's filters (or the URL's). */
export function useViewNodes(t: Tables, space: Space, view: View, filters: ViewFilters): { nodes: Node[]; kinds: Kind[] } {
  return useMemo(() => {
    const kinds = viewKinds(t, space, view);
    const kindIds = new Set(kinds.map((k) => k.id));
    const root = view.config.rootId ? descendantIds(t, view.config.rootId) : null;
    const scope = filters.milestone ? descendantIds(t, filters.milestone) : null;
    const under = root && scope ? new Set([...root].filter((id) => scope.has(id))) : (root ?? scope);
    // A status board keeps a Done column with the last two weeks of finished work, so progress stays visible.
    const recentDone = view.type === "board" && view.config.groupBy === "status" ? new Date(Date.now() - DONE_WINDOW_DAYS * 86_400_000).toISOString() : null;
    const nodes = nodesOf(t, space.id).filter(
      (n) => kindIds.has(n.kindId) && (!under || under.has(n.id)) && (view.config.includeDone || !isDone(t, n) || (recentDone !== null && n.updatedAt >= recentDone)) && matches(t, n, filters),
    );
    // A subtask shows as progress on its parent's card, not as a card of its own,
    // unless the view shows subtasks or you are filtering by person.
    if (!view.config.showSubtasks && !filters.assignees?.length && view.type !== "timeline" && view.type !== "calendar") {
      const shown = new Set(nodes.map((n) => n.id));
      return { nodes: nodes.filter((n) => !n.parentId || !shown.has(n.parentId)), kinds };
    }
    return { nodes, kinds };
  }, [t, space, view, filters]);
}

export const DONE_WINDOW_DAYS = 14;

export interface Column {
  key: string;
  label: string;
  /** A short note under the title, like "Last 14 days". */
  hint?: string;
  /** What changes when a card is dropped here. Null means cards cannot move into this column. */
  change: ((n: Node) => { status?: string; fields?: Record<string, string | null>; parentId?: string | null; assignees?: { actorId: string; role: "owner" }[]; labels?: string[] } | null) | null;
  status?: StatusDef;
  wip?: number;
  color?: string;
  actorId?: string | null;
}

const CATEGORIES: { key: StatusDef["category"]; label: string }[] = [
  { key: "todo", label: "To do" },
  { key: "active", label: "In progress" },
  { key: "review", label: "Review" },
  { key: "done", label: "Done" },
];

export function columnsFor(t: Tables, view: View, kinds: Kind[], nodes: Node[]): { columns: Column[]; columnOf: (n: Node) => string } {
  const g = view.config.groupBy;
  if (g === "status") {
    if (kinds.length === 1) {
      const k = kinds[0]!;
      const statuses = k.statuses.filter((s) => view.config.includeDone || view.type === "board" || s.category !== "done");
      const hint = view.config.includeDone ? undefined : `Last ${DONE_WINDOW_DAYS} days`;
      return {
        columns: statuses.map((s) => ({ key: s.key, label: s.label, status: s, wip: s.wip, hint: s.category === "done" ? hint : undefined, change: () => ({ status: s.key }) })),
        columnOf: (n) => n.status,
      };
    }
    // Mixed kinds share a board by status category; a drop picks the first status of that category in each kind.
    const cat = (n: Node) => t.kinds.get(n.kindId)?.statuses.find((s) => s.key === n.status)?.category ?? "todo";
    return {
      columns: CATEGORIES.filter((c) => view.config.includeDone || view.type === "board" || c.key !== "done").map((c) => ({
        key: c.key,
        label: c.label,
        hint: c.key === "done" && !view.config.includeDone ? `Last ${DONE_WINDOW_DAYS} days` : undefined,
        status: { key: c.key, label: c.label, category: c.key },
        change: (n) => {
          const s = t.kinds.get(n.kindId)?.statuses.find((x) => x.category === c.key);
          return s ? { status: s.key } : null;
        },
      })),
      columnOf: cat,
    };
  }
  if (g === "assignee") {
    const ids = new Set<string>();
    for (const n of nodes) if (n.assignees[0]) ids.add(n.assignees[0].actorId);
    const actors = [...ids].map((id) => t.actors.get(id)).filter((a) => a !== undefined).sort((a, b) => (a.type === b.type ? a.name.localeCompare(b.name) : a.type === "person" ? -1 : 1));
    return {
      columns: [
        { key: "", label: "Unassigned", actorId: null, change: () => ({ assignees: [] }) },
        ...actors.map((a) => ({ key: a.id, label: a.name, actorId: a.id, change: (n: Node) => ({ assignees: [{ actorId: a.id, role: "owner" as const }, ...n.assignees.filter((x) => x.actorId !== a.id).map((x) => ({ actorId: x.actorId, role: "owner" as const }))] }) })),
      ],
      columnOf: (n) => n.assignees[0]?.actorId ?? "",
    };
  }
  if (g === "label") {
    const labels = [...t.labels.values()].filter((l) => l.spaceId === view.spaceId).sort((a, b) => (a.rank < b.rank ? -1 : 1));
    // A card sits under its first label; dropping it on another label replaces that one and keeps the rest.
    return {
      columns: [
        { key: "", label: "No label", change: () => ({ labels: [] }) },
        ...labels.map((l) => ({ key: l.id, label: l.name, color: l.color, change: (n: Node) => ({ labels: [l.id, ...n.labels.slice(1).filter((x) => x !== l.id)] }) })),
      ],
      columnOf: (n) => n.labels[0] ?? "",
    };
  }
  if (g === "kind") {
    return { columns: kinds.map((k) => ({ key: k.id, label: k.name, change: null })), columnOf: (n) => n.kindId };
  }
  if (g === "parent") {
    const parents = [...new Set(nodes.map((n) => n.parentId).filter((p): p is string => Boolean(p)))].map((id) => t.nodes.get(id)).filter((p): p is Node => Boolean(p)).sort((a, b) => (a.rank < b.rank ? -1 : 1));
    return {
      columns: [{ key: "", label: "No parent", change: () => ({ parentId: null }) }, ...parents.map((p) => ({ key: p.id, label: p.title, change: () => ({ parentId: p.id }) }))],
      columnOf: (n) => n.parentId ?? "",
    };
  }
  if (g.startsWith("field:")) {
    const key = g.slice(6);
    const def = kinds.flatMap((k) => k.fields).find((f) => f.key === key);
    const options = def?.options ?? [];
    return {
      columns: [
        { key: "", label: `No ${def?.label.toLowerCase() ?? key}`, change: () => ({ fields: { [key]: null } }) },
        ...options.map((o) => ({ key: o, label: o, change: () => ({ fields: { [key]: o } }) })),
      ],
      columnOf: (n) => (typeof n.fields[key] === "string" ? (n.fields[key] as string) : ""),
    };
  }
  return { columns: [{ key: "all", label: "All", change: () => ({}) }], columnOf: () => "all" };
}

export function groupOptions(kinds: Kind[]): { value: string; label: string }[] {
  const selects = new Map<string, string>();
  for (const k of kinds) for (const f of k.fields) if (f.type === "select") selects.set(f.key, f.label);
  return [
    { value: "status", label: "Status" },
    { value: "assignee", label: "Assignee" },
    { value: "parent", label: "Milestone or parent" },
    { value: "label", label: "Label" },
    { value: "kind", label: "Kind" },
    ...[...selects].map(([k, l]) => ({ value: `field:${k}`, label: l })),
  ];
}

/** Where a new item made in this view goes: the milestone in scope (from the link or the view), or the view's root. */
export function newItemParent(view: View): string | null {
  return readFilters(window.location.search)?.milestone ?? view.config.filters.milestone ?? view.config.rootId ?? null;
}
